// Outline parsing and scroll-jump helpers.
// Headers use markdown-style syntax: "# Header", "## Subhead", etc.
// A space after the #'s is required — so "#hashtag" does NOT match, but "# Header" does.

import { parseLogLine } from './reminderParser';

export const MAX_HEADER_LEVEL = 4;

/**
 * Parse headers from plain text.
 *
 * Returns an array of { level, text, charOffset } in document order.
 *   level      — 1..MAX_HEADER_LEVEL (deeper levels are clamped)
 *   text       — the header text after the "# "
 *   charOffset — index of the first "#" of the header line in the source content
 */
export function parseOutline(content) {
  if (!content) return [];
  const headers = [];
  const lines = content.split('\n');
  let offset = 0;
  for (const line of lines) {
    const m = line.match(/^(#+)\s+(.+)$/);
    if (m) {
      headers.push({
        level: Math.min(m[1].length, MAX_HEADER_LEVEL),
        text: m[2].trimEnd(),
        charOffset: offset,
      });
    }
    offset += line.length + 1; // +1 for the \n removed by split
  }
  return headers;
}

/**
 * Parse `@<date>` anchor lines from plain text. Each returned entry
 * marks a navigable jump point for the LogPanel-replacement focused-
 * view rail: dated lines surface alongside `#`-headers so the user
 * can scroll a log list by date the same way they scroll a regular
 * list by section.
 *
 * Returns array of { date, restText, dateText, charOffset } in
 * document order.
 *   date       — Date the @-token resolved to (00:00 local)
 *   restText   — the line minus the @-date prefix, trimmed
 *   dateText   — the literal date substring as the user typed it
 *                ("today", "2026-05-09", "May 8" etc.)
 *   charOffset — index of the first char of the line in `content`
 *
 * `🔔`-prefixed lines are SKIPPED — those are reminders, not log
 * entries. Walter: a bell line that happens to also contain `@today`
 * stays a reminder and doesn't double-surface in the log rail.
 */
export function parseDateAnchors(content) {
  if (!content) return [];
  const anchors = [];
  if (!content.includes('@')) return anchors;
  const lines = content.split('\n');
  let offset = 0;
  for (const line of lines) {
    if (!/^(?:~~)?🔔/.test(line)) {
      const parse = parseLogLine(line);
      if (parse.isLogged && parse.parsedDate instanceof Date) {
        anchors.push({
          date: parse.parsedDate,
          restText: parse.restText,
          dateText: parse.dateText,
          charOffset: offset,
        });
      }
    }
    offset += line.length + 1;
  }
  return anchors;
}

/**
 * mergeOutlineAndDates(outline, dateAnchors) — produce a single
 * document-ordered list of navigable anchors. Tagged with `kind`
 * ('header' | 'date') so the renderer can pick the right visual
 * treatment for each.
 */
export function mergeOutlineAndDates(outline, dateAnchors) {
  const items = [];
  for (const h of (outline || [])) {
    items.push({ ...h, kind: 'header' });
  }
  for (const a of (dateAnchors || [])) {
    items.push({ ...a, kind: 'date' });
  }
  items.sort((x, y) => x.charOffset - y.charOffset);
  return items;
}

/**
 * formatDateAnchorLabel(anchor) — short readable label for an
 * `@<date>` rail row. Format mirrors the in-app date chips:
 * "today · went for run", "yesterday · groceries", or
 * "2026-05-05 · standup" for older entries. The restText is
 * truncated when long so the rail rows stay one-line.
 */
const ANCHOR_PREVIEW_CHARS = 40;
export function formatDateAnchorLabel(anchor) {
  if (!anchor || !(anchor.date instanceof Date)) return '';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const at = new Date(anchor.date);
  at.setHours(0, 0, 0, 0);
  const diff = Math.round((at - today) / 86400000);
  let datePart;
  if (diff === 0) datePart = 'today';
  else if (diff === -1) datePart = 'yesterday';
  else if (diff === 1) datePart = 'tomorrow';
  else if (diff < 0 && diff >= -6) {
    datePart = anchor.date.toLocaleDateString(undefined, { weekday: 'short' });
  } else {
    const y = anchor.date.getFullYear();
    const m = String(anchor.date.getMonth() + 1).padStart(2, '0');
    const d = String(anchor.date.getDate()).padStart(2, '0');
    datePart = `${y}-${m}-${d}`;
  }
  const rest = (anchor.restText || '').trim();
  if (!rest) return datePart;
  const truncated = rest.length > ANCHOR_PREVIEW_CHARS
    ? rest.slice(0, ANCHOR_PREVIEW_CHARS - 1).trimEnd() + '…'
    : rest;
  return `${datePart} · ${truncated}`;
}

/**
 * Move the caret in a textarea to `offset` and scroll that line into view.
 * Mirrors the behavior used by the search-jump feature.
 */
export function jumpToOffset(textarea, offset, content) {
  if (!textarea) return;
  textarea.focus();
  textarea.setSelectionRange(offset, offset);
  const lineHeight = parseInt(getComputedStyle(textarea).lineHeight) || 20;
  const lineNumber = (content.slice(0, offset).match(/\n/g) || []).length;
  textarea.scrollTop = Math.max(0, lineNumber * lineHeight - textarea.clientHeight / 2);
}
